import { GetServerSidePropsContext } from 'next';
import { IRequestContext } from '../../contexts/RequestContext';
import { isNumeric } from '../primitiveHelpers/numberHelpers';

/* ========== TYPES ========== */

export enum DeviceType {
  Unknown = 'unknown',
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop'
}

export enum AppType {
  None = 0, // default value from WF

  Website = 0,

  // / <summary>
  // / Scores app is our live-scoring platform.
  // / </summary>
  Scores = 1, // offical name
  MaxPrepsApp = 1,
  mpscoreboard = 1, // value the app sends in query string

  // / <summary>
  // / Not used atm. Dave still needs to implement.
  // / </summary>
  MaxMobile = 2,

  // / <summary>
  // / App developed to allow coach to do light-weight team management.
  // / App is being superseeded by the Teams app.
  // / </summary>
  Coach = 3, // offical name
  StatApp = 3,
  mpcoach = 3, // value the app sends in query string

  // / <summary>
  // / App being developed to connect coaches, athletes and parents.
  // / App developed to allow coach to do light-weight team management.
  // / App is being superseeded by the Teams app.
  // / </summary>
  Teams = 4, // offical name
  TeamsApp = 4,
  mpteams = 4 // value the app sends in query string
}

// const APP_PLATFORM = {
//   Unknown: 0,
//   IOS: 1,
//   Android: 2,
//   Windows: 3
// };

/* ========== UTILS ========== */

const getParamCaseInsensitive = (object: object, param: string) => {
  const key = Object.keys(object)
    .find(k => k.toLowerCase() === param.toLowerCase());

  if (typeof key === 'undefined') return;

  return object[key];
}

const getEnumValueFromQueryParam = <T extends AppType | DeviceType>(
  enumObject: any,
  queryParam: string | number,
  defaultValue: T
): T => {
  if (typeof queryParam !== 'string' && typeof queryParam !== 'number')
    return defaultValue;

  // If query param is a numeric, lookup and return the enum value
  if (typeof queryParam === 'number' || isNumeric(queryParam)) {
    // This will also convert numeric strings to the number equivalent
    // i.e. AppType[AppType['0'] = 'Website'] = 0
    return enumObject[enumObject[queryParam]] ?? defaultValue;
  }

  // i.e. AppType['Website'] = 0
  return getParamCaseInsensitive(enumObject, queryParam) ?? defaultValue;
};

/* ========== WRAPPERS ========== */

export const parseAppTypeQueryParamFallbackToDefault = qp =>
  getEnumValueFromQueryParam(AppType, qp, AppType.None);

export const parseDeviceTypeQueryParamFallbackToDefault = qp =>
  getEnumValueFromQueryParam(DeviceType, qp, DeviceType.Desktop);

/* ========== DEVICE TYPE HELPERS ========== */

const AKAMAI_HEADER = 'x-akamai-device-characteristics';

const tryGetDeviceTypeFromAkamaiHeader = (
  nextContext: GetServerSidePropsContext
): DeviceType | undefined => {
  const deviceTypeHeader = nextContext?.req?.headers[AKAMAI_HEADER];

  if (typeof deviceTypeHeader !== 'string') return;

  // Split the headers on the semi-colon
  const items = deviceTypeHeader.toLowerCase().split(';');

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.includes('is_mobile=true')) return DeviceType.Mobile;
    if (item.includes('is_tablet=true')) return DeviceType.Tablet;
    if (item.includes('is_desktop=true')) return DeviceType.Desktop;
  }
};

const tryGetDeviceTypeFromUserAgentHeader = (
  nextContext: GetServerSidePropsContext
): DeviceType | undefined => {
  // This should help with local dev where we aren't routing through Fastly
  const userAgentHeader = nextContext.req.headers['user-agent'];

  if (typeof userAgentHeader !== 'string') return;

  // for now, this is overkill - leave it out and if we ever need better support
  // for local dev we can uncomment.
  // if (
  //   userAgentHeader?.match(/(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i)
  // ) {
  //   return DeviceType.Tablet;
  // }

  if (
    userAgentHeader?.match(/Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/)
  ) {
    return DeviceType.Mobile;
  }
};

const getDeviceType = (nextContext: GetServerSidePropsContext): DeviceType => {
  let deviceType;

  deviceType = tryGetDeviceTypeFromAkamaiHeader(nextContext);
  if (deviceType) return deviceType;

  deviceType = tryGetDeviceTypeFromUserAgentHeader(nextContext);
  if (deviceType) return deviceType;

  return parseDeviceTypeQueryParamFallbackToDefault(
    nextContext?.query?.devicetype
  );
};

/* ========== APP TYPE HELPERS ========== */

const getAppType = (nextContext: GetServerSidePropsContext): AppType =>
  parseAppTypeQueryParamFallbackToDefault(nextContext?.query?.apptype);

/* ========== MAIN FUNCTION ========== */

const getRequestContextFromNextContext = (
  nextContext: GetServerSidePropsContext
): IRequestContext => ({
  deviceType: getDeviceType(nextContext),
  appType: getAppType(nextContext)
  // devicePlatform: getDevicePlatform(nextContext) // not implemented
});

export default getRequestContextFromNextContext;
